import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { tenantService } from "../../services/tenantService";
import { provisioningService } from "../../services/provisioningService";
import { Tenant } from "../../types";
import { required_error } from "../../services/shared";

const editSchema = z.object({
  name: z.string({ required_error }).min(2, "Minimo 2 caratteri"),
  wa_phone_number_id: z.string({ required_error }).min(1, "Campo obbligatorio"),
  wa_verify_token: z.string({ required_error }).min(1, "Campo obbligatorio"),
});

const tokenSchema = z.object({
  wa_access_token_value: z.string().optional(),
  openai_api_key_value: z
    .string()
    .optional()
    .refine((v) => !v || v.startsWith("sk-"), {
      message: 'La chiave OpenAI deve iniziare con "sk-"',
    }),
});

type EditForm = z.infer<typeof editSchema>;
type TokenForm = z.infer<typeof tokenSchema>;

export default function TenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [tokenSuccess, setTokenSuccess] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const editForm = useForm<EditForm>({ resolver: zodResolver(editSchema) });
  const tokenForm = useForm<TokenForm>({ resolver: zodResolver(tokenSchema) });

  useEffect(() => {
    if (!tenantId) return;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await tenantService.getById(tenantId);
        const t = res.data.data;
        setTenant(t);
        editForm.reset({
          name: t.name,
          wa_phone_number_id: t.wa_phone_number_id,
          wa_verify_token: t.wa_verify_token,
        });
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento del tenant");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = editForm.handleSubmit(async (data) => {
    if (!tenantId) return;
    setError(null);
    setSaveSuccess(false);
    try {
      await tenantService.update(tenantId, data);
      setSaveSuccess(true);
    } catch (e) {
      console.error(e);
      setError("Errore durante il salvataggio");
    }
  });

  const handleRotateTokens = tokenForm.handleSubmit(async (data) => {
    if (!tenantId) return;
    setError(null);
    setTokenSuccess(false);
    const payload: { wa_access_token_value?: string; openai_api_key_value?: string } = {};
    if (data.wa_access_token_value) payload.wa_access_token_value = data.wa_access_token_value;
    if (data.openai_api_key_value) payload.openai_api_key_value = data.openai_api_key_value;
    try {
      await provisioningService.rotateTokens(tenantId, payload);
      setTokenSuccess(true);
      tokenForm.reset();
    } catch (e) {
      console.error(e);
      setError("Errore durante la rotazione dei token");
    }
  });

  const handleDeactivate = async () => {
    if (!tenantId) return;
    if (!window.confirm("Disattivare questo tenant? L'operazione è irreversibile.")) return;
    setDeactivating(true);
    try {
      await provisioningService.deactivateTenant(tenantId);
      navigate("/chatbot/tenants");
    } catch (e) {
      console.error(e);
      setError("Errore durante la disattivazione");
      setDeactivating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tenant) {
    return (
      <div className="container">
        <Alert severity="error">Tenant non trovato</Alert>
      </div>
    );
  }

  return (
    <div className="container">
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <FormLabel label={`Tenant: ${tenant.name}`} />
        <Chip
          label={tenant.active ? "Attivo" : "Inattivo"}
          color={tenant.active ? "success" : "default"}
          size="small"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Edit form */}
      <Box
        sx={{
          background: "white",
          border: "1px solid #d1d1d1",
          borderRadius: ".5em",
          p: 3,
          mb: 2,
        }}
        className="flex-col gap-2"
      >
        <FormLabel label="Informazioni base" />
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            Modifiche salvate
          </Alert>
        )}
        <form onSubmit={handleSave} className="flex-col gap-2">
          <Controller
            name="name"
            control={editForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Nome tenant"
                error={!!editForm.formState.errors.name}
                helperText={editForm.formState.errors.name?.message}
              />
            )}
          />
          <div className="flex-row gap-2">
            <Controller
              name="wa_phone_number_id"
              control={editForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="WhatsApp Phone Number ID"
                  error={!!editForm.formState.errors.wa_phone_number_id}
                  helperText={editForm.formState.errors.wa_phone_number_id?.message}
                />
              )}
            />
            <Controller
              name="wa_verify_token"
              control={editForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="WhatsApp Verify Token"
                  error={!!editForm.formState.errors.wa_verify_token}
                  helperText={editForm.formState.errors.wa_verify_token?.message}
                />
              )}
            />
          </div>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/chatbot/tenants")}
            >
              Indietro
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={editForm.formState.isSubmitting}
            >
              {editForm.formState.isSubmitting ? <CircularProgress size={20} /> : "Salva modifiche"}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Token rotation */}
      <Box
        sx={{
          background: "white",
          border: "1px solid #d1d1d1",
          borderRadius: ".5em",
          p: 3,
          mb: 2,
        }}
        className="flex-col gap-2"
      >
        <FormLabel label="Rotazione token" />
        {tokenSuccess && (
          <Alert severity="success" sx={{ mb: 1 }}>
            Token aggiornati con successo
          </Alert>
        )}
        <form onSubmit={handleRotateTokens} className="flex-col gap-2">
          <Controller
            name="wa_access_token_value"
            control={tokenForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="password"
                label="Nuovo WhatsApp Access Token (opzionale)"
                error={!!tokenForm.formState.errors.wa_access_token_value}
                helperText={tokenForm.formState.errors.wa_access_token_value?.message}
              />
            )}
          />
          <Controller
            name="openai_api_key_value"
            control={tokenForm.control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                type="password"
                label="Nuova OpenAI API Key (opzionale, sk-...)"
                error={!!tokenForm.formState.errors.openai_api_key_value}
                helperText={tokenForm.formState.errors.openai_api_key_value?.message}
              />
            )}
          />
          <Box>
            <Button
              variant="contained"
              type="submit"
              disabled={tokenForm.formState.isSubmitting}
            >
              {tokenForm.formState.isSubmitting ? <CircularProgress size={20} /> : "Ruota token"}
            </Button>
          </Box>
        </form>
      </Box>

      {/* Danger zone */}
      {tenant.active && (
        <Box
          sx={{
            background: "white",
            border: "1px solid #d32f2f",
            borderRadius: ".5em",
            p: 3,
          }}
          className="flex-col gap-2"
        >
          <FormLabel label="Zona pericolosa" />
          <p style={{ margin: 0, color: "#666", fontSize: "0.875rem" }}>
            La disattivazione del tenant è irreversibile e interrompe il servizio WhatsApp.
          </p>
          <Box>
            <Button
              variant="outlined"
              color="error"
              disabled={deactivating}
              onClick={handleDeactivate}
            >
              {deactivating ? <CircularProgress size={20} /> : "Disattiva tenant"}
            </Button>
          </Box>
        </Box>
      )}
    </div>
  );
}
